import React, { FC, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { _t, lockBodyScroll } from 'utils';
import type { AnalyticsPopup } from '../analyticsSupport';
import styles from './styles.module.scss';

type Props = {
  item: AnalyticsPopup | null;
  onClose: () => void;
};

const withEnd = (text: string, ends: string[] | undefined, index: number) => (
  `${_t(text)}${ends?.[index] || ''}`
);

const markerLabel = (kind: 'dash' | 'number' | 'decimal', index: number) => {
  if (kind === 'dash') {
    return '-';
  }
  if (kind === 'decimal') {
    return `${index + 1}.`;
  }
  return `${index + 1})`;
};

const ServiceTextPopup: FC<Props> = ({ item, onClose }) => {
  useEffect(() => {
    if (!item) {
      return undefined;
    }
    return lockBodyScroll();
  }, [item]);

  useEffect(() => {
    if (!item) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, onClose]);

  if (!item) {
    return null;
  }

  return createPortal(
    (
      <div className={styles.root} role="dialog" aria-modal="true" aria-labelledby="service-text-popup-title">
        <button
          type="button"
          className={styles.backdrop}
          aria-label={_t('Закрыть')}
          onClick={onClose}
        />
        <div className={styles.panel}>
          <button
            type="button"
            className={styles.close}
            aria-label={_t('Закрыть')}
            onClick={onClose}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M1 1l14 14M15 1L1 15" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
          <div className={styles.body}>
            {item.leadOnNextLine ? (
              <>
                <p id="service-text-popup-title" className={styles.paragraph}>
                  <strong>{_t(item.title)}</strong>
                </p>
                {item.lead ? <p className={styles.paragraph}>{_t(item.lead)}</p> : null}
              </>
            ) : (
              <p id="service-text-popup-title" className={styles.paragraph}>
                <strong>{`${item.index} ${_t(item.title)}`}</strong>
                {item.separator}
                {_t(item.lead)}
              </p>
            )}
            {item.points?.length ? (
              <ul className={styles.list}>
                {item.points.map((point, index) => (
                  <li key={point} className={styles.item}>
                    <span className={styles.mark}>{markerLabel(item.pointsMarker || 'dash', index)}</span>
                    <span>{withEnd(point, item.pointEnds, index)}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {item.note?.length ? (
              <p className={styles.paragraph}>
                {item.note.map((part) => (
                  part.bold
                    ? <strong key={part.text}>{_t(part.text)}</strong>
                    : <span key={part.text}>{_t(part.text)}</span>
                ))}
              </p>
            ) : null}
            {item.advantagesTitle ? (
              <p className={styles.paragraph}>{_t(item.advantagesTitle)}</p>
            ) : null}
            <ul className={styles.list}>
              {item.advantages.map((line, index) => (
                <li key={line} className={styles.item}>
                  <span className={styles.mark}>{markerLabel(item.advantagesMarker || 'number', index)}</span>
                  <span>{withEnd(line, item.advantageEnds, index)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    ),
    document.body,
  );
};

export default ServiceTextPopup;
